import { NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import {
    hasModuleAccess,
    hasAnyRole,
    isAuthenticated,
    canReadModule,
} from "../utils/authUtils";
import "./Sidebar.css";
import NavIcon from "./navIcons";
import MpaLogo from "../components/MpaLogo";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function SidebarLink({ to, end, className = "", active, onClick, icon, label, badge, title }) {
    const inner = (
        <>
            <span className="mpa-sidebar__link-icon">
                <NavIcon name={icon} />
            </span>
            <span className="mpa-sidebar__link-label" title={title || label}>
                {label}
            </span>
            {badge != null && badge !== "" ? (
                <span className="mpa-sidebar__badge">{badge}</span>
            ) : null}
        </>
    );

    if (to) {
        return (
            <NavLink
                to={to}
                end={end}
                title={title || label}
                className={({ isActive }) =>
                    `mpa-sidebar__link ${isActive || active ? "active" : ""} ${className}`.trim()
                }
                onClick={onClick}
            >
                {inner}
            </NavLink>
        );
    }

    return (
        <span className={`mpa-sidebar__link ${className}`.trim()} title={title || label}>
            {inner}
        </span>
    );
}

function SubmenuToggle({ open, active, onClick, icon, label, badge }) {
    return (
        <button
            type="button"
            className={`mpa-sidebar__link ${active ? "active" : ""}`}
            onClick={onClick}
            aria-expanded={open}
        >
            <span className="mpa-sidebar__link-icon">
                <NavIcon name={icon} />
            </span>
            <span className="mpa-sidebar__link-label">{label}</span>
            {badge != null && badge !== "" ? (
                <span className="mpa-sidebar__badge">{badge}</span>
            ) : null}
            <NavIcon name="icon-chevron-down" className="mpa-sidebar__link-chevron" />
        </button>
    );
}

export default function Sidebar({ isOpen, setIsOpen }) {
    const location = useLocation();

    const [isSetupOpen, setIsSetupOpen] = useState(true);
    const [isEntitiesOpen, setIsEntitiesOpen] = useState(true);
    const [entities, setEntities] = useState([]);
    const [entitiesLoading, setEntitiesLoading] = useState(true);

    const refreshEntities = async () => {
        if (!hasModuleAccess("entities")) return;

        try {
            const token = localStorage.getItem("token");
            if (!token) return;

            const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
            const userRoles = currentUser?.roles || [];
            const isSuperAdmin = userRoles.includes("superadmin");

            const endpoint = isSuperAdmin ? "/entities" : "/entities/my";
            const res = await axios.get(`${API_BASE}${endpoint}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEntities(res.data || []);
        } catch (error) {
            console.error("Failed to refresh entities:", error);
        }
    };

    if (typeof window !== "undefined") {
        window.refreshSidebarEntities = refreshEntities;
    }

    const setupItems = [
        { key: "users", label: "Users", path: "/users/list", icon: "icon-users" },
        { key: "roles", label: "Roles & Permissions", path: "/roles/list", icon: "icon-lock" },
        { key: "entities", label: "Entities", path: "/entities/list", icon: "icon-briefcase" },
        {
            key: "categories",
            label: "Categories",
            path: "/categories/list",
            icon: "icon-tag",
            adminOnly: true,
        },
    ];

    const allowedSetupItems = setupItems.filter((item) => {
        if (item.adminOnly && !hasAnyRole(["superadmin", "admin"])) return false;
        if (item.key === "categories") {
            return hasAnyRole(["superadmin", "admin"]) && hasModuleAccess("entities");
        }
        if (!hasModuleAccess(item.key)) return false;
        if (item.key === "users" && hasAnyRole(["employee"])) return false;
        return true;
    });

    const setupRoutes = allowedSetupItems.map((item) => item.path.split("/")[1]);
    const isSetupRouteActive = setupRoutes.some((route) =>
        location.pathname.startsWith(`/${route}`)
    );

    useEffect(() => {
        const fetchEntities = async () => {
            if (!hasModuleAccess("entities")) {
                setEntitiesLoading(false);
                return;
            }

            try {
                setEntitiesLoading(true);
                const token = localStorage.getItem("token");
                if (!token) {
                    setEntitiesLoading(false);
                    return;
                }

                const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
                const userRoles = currentUser?.roles || [];
                const isSuperAdmin = userRoles.includes("superadmin");

                const endpoint = isSuperAdmin ? "/entities" : "/entities/my";
                const res = await axios.get(`${API_BASE}${endpoint}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setEntities(res.data || []);
            } catch (error) {
                console.error("Failed to fetch entities for sidebar:", error);
                if (error.response?.status === 401) setEntities([]);
            } finally {
                setEntitiesLoading(false);
            }
        };

        fetchEntities();
    }, []);

    useEffect(() => {
        if (window.innerWidth >= 992) return undefined;

        document.body.style.overflow = isOpen ? "hidden" : "";
        document.body.classList.toggle("mpa-sidebar-open", isOpen);
        return () => {
            document.body.style.overflow = "";
            document.body.classList.remove("mpa-sidebar-open");
        };
    }, [isOpen]);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape" && isOpen && setIsOpen) {
                setIsOpen(false);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isOpen, setIsOpen]);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 992 && isOpen && setIsOpen) {
                setIsOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [isOpen, setIsOpen]);

    if (!isAuthenticated() || location.pathname === "/unauthorized") {
        return null;
    }

    const closeOnMobile = () => {
        if (window.innerWidth < 992 && setIsOpen) {
            setIsOpen(false);
        }
    };

    const isEntityDetails = location.pathname.startsWith("/entities/");

    return (
        <>
            <div
                className={`mpa-sidebar-overlay ${isOpen ? "show" : ""}`}
                onClick={() => setIsOpen && setIsOpen(false)}
                aria-hidden="true"
            />

            <aside
                className={`mpa-sidebar ${isOpen ? "open" : ""}`}
                aria-label="Main navigation"
            >
                <div className="mpa-sidebar__mobile-header">
                    <div className="mpa-sidebar__brand">
                        <MpaLogo size={30} variant="icon" />
                        <div className="mpa-sidebar__brand-text">
                            <span className="mpa-sidebar__brand-title">Navigation</span>
                            <span className="mpa-sidebar__brand-sub">My Personal Assistant</span>
                        </div>
                    </div>
                    <button
                        type="button"
                        className="mpa-sidebar__close"
                        onClick={() => setIsOpen && setIsOpen(false)}
                        aria-label="Close menu"
                    >
                        <NavIcon name="icon-x" />
                    </button>
                </div>

                <div className="mpa-sidebar__desktop-brand">
                    <div className="mpa-sidebar__brand">
                        <MpaLogo size={30} variant="icon" />
                        <div className="mpa-sidebar__brand-text">
                            <span className="mpa-sidebar__brand-title">Menu</span>
                            <span className="mpa-sidebar__brand-sub">Quick access</span>
                        </div>
                    </div>
                </div>

                <div className="mpa-sidebar__scroll">
                    <p className="mpa-sidebar__section">Overview</p>
                    <ul className="mpa-sidebar__nav">
                        {canReadModule("dashboard") && hasAnyRole(["superadmin", "admin", "manager"]) && (
                            <li className="mpa-sidebar__item">
                                <SidebarLink
                                    to="/admins/dashboard"
                                    icon="icon-home"
                                    label="Dashboard"
                                    active={
                                        location.pathname.startsWith("/admins/dashboard") ||
                                        isEntityDetails
                                    }
                                    onClick={closeOnMobile}
                                />
                            </li>
                        )}

                        {hasAnyRole(["superadmin"]) && (
                            <li className="mpa-sidebar__item">
                                <SidebarLink
                                    to="/admins/attendance-map"
                                    icon="icon-map-pin"
                                    label="Team Locations"
                                    onClick={closeOnMobile}
                                />
                            </li>
                        )}

                        {hasAnyRole(["customer"]) && (
                            <li className="mpa-sidebar__item">
                                <SidebarLink
                                    to="/customers/dashboard"
                                    icon="icon-home"
                                    label="Customer Dashboard"
                                    active={
                                        location.pathname.startsWith("/customers/dashboard") ||
                                        isEntityDetails
                                    }
                                    onClick={closeOnMobile}
                                />
                            </li>
                        )}

                        {hasAnyRole(["employee"]) &&
                            !hasAnyRole(["superadmin", "admin", "manager"]) && (
                                <li className="mpa-sidebar__item">
                                    <SidebarLink
                                        to="/employees/dashboard"
                                        icon="icon-home"
                                        label="Employee Dashboard"
                                        active={
                                            location.pathname.startsWith("/employees/dashboard") ||
                                            isEntityDetails
                                        }
                                        onClick={closeOnMobile}
                                    />
                                </li>
                            )}
                    </ul>

                    {hasModuleAccess("entities") && (
                        <>
                            <p className="mpa-sidebar__section">Workspace</p>
                            <ul className="mpa-sidebar__nav">
                                <li className={`mpa-sidebar__item has-sub ${isEntitiesOpen ? "open" : ""}`}>
                                    <SubmenuToggle
                                        open={isEntitiesOpen}
                                        active={location.pathname.startsWith("/entities")}
                                        onClick={() => setIsEntitiesOpen((prev) => !prev)}
                                        icon="icon-briefcase"
                                        label="Entities"
                                        badge={!entitiesLoading && entities.length ? entities.length : null}
                                    />

                                    <ul className="mpa-sidebar__submenu">
                                        {hasAnyRole(["superadmin", "admin"]) && (
                                        <li>
                                            <SidebarLink
                                                to="/entities/list"
                                                icon="icon-list"
                                                label={hasAnyRole(["superadmin"]) ? "All Entities" : "My Entities"}
                                                onClick={closeOnMobile}
                                            />
                                        </li>
                                        )}
                                        <div className="mpa-sidebar__submenu-scroll">
                                            {entitiesLoading ? (
                                                <li>
                                                    <span className="mpa-sidebar__link mpa-sidebar__link--muted">
                                                        <span className="mpa-sidebar__link-icon">
                                                            <NavIcon name="icon-loader" spin />
                                                        </span>
                                                        <span className="mpa-sidebar__link-label">Loading entities…</span>
                                                    </span>
                                                </li>
                                            ) : entities.length === 0 ? (
                                                <li>
                                                    <span className="mpa-sidebar__link mpa-sidebar__link--muted">
                                                        <span className="mpa-sidebar__link-icon">
                                                            <NavIcon name="icon-info" />
                                                        </span>
                                                        <span className="mpa-sidebar__link-label">No entities found</span>
                                                    </span>
                                                </li>
                                            ) : (
                                                entities.map((entity) => (
                                                    <li key={entity._id}>
                                                        <SidebarLink
                                                            to={`/entities/${entity._id}`}
                                                            icon="icon-box"
                                                            label={entity.entityName}
                                                            title={entity.entityName}
                                                            onClick={closeOnMobile}
                                                        />
                                                    </li>
                                                ))
                                            )}
                                        </div>
                                    </ul>
                                </li>
                            </ul>
                        </>
                    )}

                    {hasAnyRole(["superadmin", "admin"]) && allowedSetupItems.length > 0 && (
                        <>
                            <p className="mpa-sidebar__section">Configuration</p>
                            <ul className="mpa-sidebar__nav">
                                {allowedSetupItems.length === 1 ? (
                                    <li className="mpa-sidebar__item">
                                        <SidebarLink
                                            to={allowedSetupItems[0].path}
                                            icon={allowedSetupItems[0].icon}
                                            label={allowedSetupItems[0].label}
                                            onClick={closeOnMobile}
                                        />
                                    </li>
                                ) : (
                                    <li className={`mpa-sidebar__item has-sub ${isSetupOpen ? "open" : ""}`}>
                                        <SubmenuToggle
                                            open={isSetupOpen}
                                            active={isSetupRouteActive}
                                            onClick={() => setIsSetupOpen((prev) => !prev)}
                                            icon="icon-settings"
                                            label="Setup"
                                        />

                                        <ul className="mpa-sidebar__submenu">
                                            {allowedSetupItems.map((item) => (
                                                <li key={item.key}>
                                                    <SidebarLink
                                                        to={item.path}
                                                        icon={item.icon}
                                                        label={item.label}
                                                        onClick={closeOnMobile}
                                                    />
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                )}
                            </ul>
                        </>
                    )}
                </div>
            </aside>
        </>
    );
}
